import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User, UserRole } from '../../models/user';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    public sessionRole: string;

    constructor(private http: HttpClient) {
        this.currentUserSubject = new BehaviorSubject<User>(
            JSON.parse(localStorage.getItem('currentUser'))
        );
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    login(email: string, password: string, rememberMe: boolean) {
        return this.http
            .post<any>(`${environment.apiUrl}/auth/login/`, {
                email,
                password,
                rememberMe
            })
            .pipe(
                map(res => {
                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    this.setTokens(res);
                    return res;
                })
            );
    }

    setTokens(res) {
        localStorage.setItem('currentUser', JSON.stringify(res));
        localStorage.setItem('rememberMeToken', res.rememberMeToken);
        this.currentUserSubject.next(res);
    }

    setSessionRole(role: string) {
        localStorage.setItem('sessionRole', JSON.stringify(role));
        this.sessionRole = role;
    }

    getSessionRole() {
        return this.sessionRole || JSON.parse(localStorage.getItem('sessionRole'));
    }

    hasAdminRole() {
        return (
            this.currentUserValue.roles.includes(UserRole.ADMIN) ||
            this.currentUserValue.roles.includes(UserRole.SUPER_ADMIN)
        );
    }

    isAdmin() {
        const sessionRole = this.getSessionRole();
        return sessionRole === UserRole.ADMIN || sessionRole === UserRole.SUPER_ADMIN;
    }

    isClient() {
        return this.getSessionRole() === UserRole.CLIENT;
    }

    isSupplier() {
        return this.getSessionRole() === UserRole.SUPPLIER;
    }

    isPreviewMode() {
        return this.isAdmin() || this.isSupplier();
    }

    logout() {
        // remove user from local storage to log user out
        localStorage.removeItem('currentUser');
        localStorage.removeItem('rememberMeToken');
        localStorage.removeItem('sessionRole');

        this.currentUserSubject.next(null);
    }

    register(user: User) {
        return this.http.post(`${environment.apiUrl}/auth/register/`, user);
    }

    getAll() {
        return this.http.get<User[]>(`${environment.apiUrl}/users`);
    }

    getById(id: string) {
        return this.http.get<User>(`${environment.apiUrl}/users/${id}`);
    }

    update(id, params) {
        return this.http.put(`${environment.apiUrl}/users/${id}`, params).pipe(
            map(x => {
                // update stored user if the logged in user updated their own record
                if (id == this.currentUserValue.id) {
                    // update local storage
                    const user = { ...this.currentUserValue, ...params };
                    localStorage.setItem('user', JSON.stringify(user));

                    // publish updated user to subscribers
                    this.currentUserSubject.next(user);
                }
                return x;
            })
        );
    }

    delete(id: string) {
        return this.http.delete(`${environment.apiUrl}/users/${id}`).pipe(
            map(x => {
                // auto logout if the logged in user deleted their own record
                if (id == this.currentUserValue.id) {
                    this.logout();
                }
                return x;
            })
        );
    }

    forgetPassword(email: string) {
        return this.http.post<any>(`${environment.apiUrl}/auth/recover/email/`, {
            email
        });
    }

    resetPassword(newPassword: string, token: string) {
        return this.http.post<any>(`${environment.apiUrl}/auth/reset/password/`, {
            newPassword,
            token
        });
    }

    verifyEmail(token: string) {
        return this.http.get<any>(`${environment.apiUrl}/auth/verify/email/${token}`);
    }
}
